import { Alert, Button, Card, Form, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationSectionSelect,
  EducationSessionSelect,
} from '@/pages/education/components/EducationLookupSelects';

type Grid = {
  id: string;
  section_id: string;
  section_name?: string;
  grade_code?: string;
  status: string;
  slot_count?: number;
};

export default function TimetableGridsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.timetable.manage') || user?.permissions?.includes('education.admin')
  );
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [sectionId, setSectionId] = useState<string | undefined>();

  const list = useQuery({
    queryKey: ['education-timetable-grids', sessionId],
    enabled: Boolean(sessionId),
    queryFn: async () => {
      const res = await api.get<Grid[]>('/education/timetable/grids', {
        params: { session_id: sessionId },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const ensure = useMutation({
    mutationFn: () =>
      api.post<{ grid: { id: string } }>('/education/timetable/grids/ensure', {
        session_id: sessionId,
        section_id: sectionId,
      }),
    onSuccess: (res) => {
      const body = res.data as { grid?: { id?: string }; id?: string };
      const gridId = body?.grid?.id || body?.id;
      queryClient.invalidateQueries({ queryKey: ['education-timetable-grids'] });
      if (gridId) navigate(`/education/timetable/grids/${gridId}`);
      else message.success(t('timetableGridReady'));
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('timetableGrids')}
      description={t('timetableGridsIntro')}
      extra={<Button onClick={() => navigate('/education/timetable')}>{t('backToTimetable')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline">
            <Form.Item label={t('session')} required>
              <EducationSessionSelect
                style={{ minWidth: 200 }}
                value={sessionId}
                onChange={(v) => {
                  setSessionId(v || undefined);
                  setSectionId(undefined);
                }}
              />
            </Form.Item>
            {canManage ? (
              <>
                <Form.Item label={t('section')}>
                  <EducationSectionSelect
                    sessionId={sessionId}
                    style={{ minWidth: 180 }}
                    value={sectionId}
                    onChange={(v) => setSectionId(v || undefined)}
                    disabled={!sessionId}
                  />
                </Form.Item>
                <Button
                  type="primary"
                  disabled={!sessionId || !sectionId}
                  loading={ensure.isPending}
                  onClick={() => ensure.mutate()}
                >
                  {t('openSectionTimetable')}
                </Button>
              </>
            ) : null}
          </Form>
        </Card>

        {!sessionId ? (
          <Alert type="info" showIcon message={t('timetablePickSession')} />
        ) : (
          <Card size="small">
            <Table
              rowKey="id"
              size="small"
              loading={list.isLoading}
              dataSource={list.data ?? []}
              pagination={{ pageSize: 25 }}
              columns={[
                {
                  title: t('section'),
                  render: (_: unknown, row: Grid) =>
                    `${row.grade_code ? `${row.grade_code} ` : ''}${row.section_name || row.section_id}`,
                },
                {
                  title: t('status'),
                  dataIndex: 'status',
                  width: 120,
                  render: (v: string) => (
                    <Tag color={v === 'published' ? 'success' : 'default'}>{v}</Tag>
                  ),
                },
                { title: t('slots'), dataIndex: 'slot_count', width: 80 },
                {
                  title: t('actions'),
                  width: 120,
                  render: (_: unknown, row: Grid) => (
                    <Button size="small" onClick={() => navigate(`/education/timetable/grids/${row.id}`)}>
                      {t('open')}
                    </Button>
                  ),
                },
              ]}
            />
          </Card>
        )}
      </Space>
    </EducationPageChrome>
  );
}
